import * as React from "react"
import { ChevronDown, Plus, X } from "lucide-react"

import { cn } from "@/lib/utils"

/*
  FilterChip — toolbar filter in two states:
    unset → dashed  "+ Location"            (or "Location ⌄" with variant="select")
    set   → solid   "Location: AD Store  ×"

    <Popover>
      <PopoverTrigger asChild>
        <FilterChip label="Location" value={location} onClear={() => setLocation(null)} />
      </PopoverTrigger>
      <PopoverContent>…</PopoverContent>
    </Popover>

  The ref and any extra props (onClick, aria-*, data-state from Radix triggers) go to the main
  button, so it works as an `asChild` trigger. The × is a separate button (onClear).
  Props: label, value (node; empty/null = unset), onClear, variant 'add' (default) | 'select',
  icon (replaces the + icon), clearLabel, className.
*/
const FilterChip = React.forwardRef(({
  label,
  value,
  onClear,
  variant = "add",
  icon: Icon,
  clearLabel,
  className,
  ...props
}, ref) => {
  const isSet = !(value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0))
  const displayValue = Array.isArray(value) ? value.join(", ") : value

  const base =
    "inline-flex h-[30px] max-w-[260px] items-center gap-1.5 whitespace-nowrap text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

  if (!isSet) {
    const LeadIcon = Icon || (variant === "add" ? Plus : null)
    return (
      <button
        ref={ref}
        type="button"
        data-state-set="false"
        className={cn(
          base,
          "rounded-full border border-dashed border-[color:var(--line-dashed)] bg-surface px-[11px] text-ink-2 hover:border-[color:var(--ink-4)] hover:bg-surface-hover hover:text-ink",
          className
        )}
        {...props}
      >
        {LeadIcon ? <LeadIcon className="h-[13px] w-[13px] shrink-0" aria-hidden="true" /> : null}
        <span className="truncate">{label}</span>
        {variant === "select" ? <ChevronDown className="h-[13px] w-[13px] shrink-0 text-ink-3" aria-hidden="true" /> : null}
      </button>
    )
  }

  return (
    <span
      className={cn(
        "inline-flex h-[30px] max-w-[300px] items-center rounded-full border border-[color:var(--brand-soft-border)] bg-brand-soft text-[color:var(--brand-ink)]",
        className
      )}
      data-state-set="true"
    >
      <button
        ref={ref}
        type="button"
        // h-full: the outer pill sets the height (30px, or what className asks for, e.g. max-sm:h-9)
        className={cn(base, "h-full min-w-0 rounded-full pl-[11px]", onClear ? "pr-1" : "pr-[11px]")}
        {...props}
      >
        {Icon ? <Icon className="h-[13px] w-[13px] shrink-0" aria-hidden="true" /> : null}
        <span className="truncate">
          <span className="font-semibold">{label}:</span>{" "}
          <span className="font-bold text-[color:var(--ink)]">{displayValue}</span>
        </span>
      </button>
      {onClear ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onClear()
          }}
          aria-label={clearLabel || `Clear ${label} filter`}
          className="mr-1 grid h-6 w-6 shrink-0 place-items-center rounded-full text-ink-3 transition-colors hover:bg-[color:var(--brand-soft-border)] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      ) : null}
    </span>
  )
})
FilterChip.displayName = "FilterChip"

export { FilterChip }
